/**
 * batch-39 gesprekken (FO-332) — deadline-vorm-resolutie voor actiepunten.
 *
 * Het gespreksverslag-template gebruikt vrije termijnen ("korte termijn",
 * "na juni 2026", "eind maand 5"). De invoer kent vier vormen; elke vorm
 * resolvet naar een RICHTDATUM (yyyy-MM-dd, NL-kalenderdag) voor de
 * herinnerings-cron, terwijl het document de BEWOORDING print.
 *
 * Pure module: werkt uitsluitend op ymd-/ym-strings (geen Date-constructie op
 * lokale tijd), dus tz-vast en unit-testbaar. De caller levert `vandaagYmd`
 * als NL-kalenderdag (bv. via utcToAmsterdamWallclock).
 */

import type { GespreksverslagDeadlineVorm } from '@streep/types'

/** Hoeveel dagen "korte termijn" vooruit ligt (FO-332 §3: +2 weken). */
export const KORTE_TERMIJN_DAGEN = 14

const NL_MAANDEN = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december',
] as const

export interface DeadlineVormInput {
  vorm: GespreksverslagDeadlineVorm
  /** Vandaag als NL-kalenderdag (yyyy-MM-dd) — anker voor KORTE_TERMIJN/EINDE_MAAND. */
  vandaagYmd: string
  /** Bij DATUM: de gekozen datum (yyyy-MM-dd). */
  datumYmd?: string | null
  /** Bij EINDE_MAAND/NA_MAAND: de gekozen maand (yyyy-MM). EINDE_MAAND zonder maand = de huidige maand. */
  maandYm?: string | null
}

export interface DeadlineResolutie {
  /** De richtdatum (yyyy-MM-dd) voor de herinnering. */
  richtDatumYmd: string
  /** De bewoording zoals het document hem print. */
  bewoording: string
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

function ymdParts(ymd: string): { jaar: number; maand: number; dag: number } {
  const [j, m, d] = ymd.split('-').map((x) => parseInt(x, 10))
  return { jaar: j, maand: m, dag: d }
}

/** Dagen optellen bij een ymd, kalender-correct via Date.UTC (geen tz-invloed). */
export function ymdPlusDagen(ymd: string, dagen: number): string {
  const { jaar, maand, dag } = ymdParts(ymd)
  const t = new Date(Date.UTC(jaar, maand - 1, dag + dagen))
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`
}

/** Laatste dag van een maand (yyyy-MM → yyyy-MM-dd). */
export function laatsteDagVanMaand(ym: string): string {
  const [jaar, maand] = ym.split('-').map((x) => parseInt(x, 10))
  // Dag 0 van de volgende maand = laatste dag van deze maand.
  const t = new Date(Date.UTC(jaar, maand, 0))
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`
}

/** Eerste dag ná een maand (yyyy-MM → yyyy-MM-dd van de 1e van de volgende maand). */
export function eersteDagNaMaand(ym: string): string {
  const [jaar, maand] = ym.split('-').map((x) => parseInt(x, 10))
  const t = new Date(Date.UTC(jaar, maand, 1))
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`
}

/** "2026-06" → "juni 2026" (voor de bewoording). */
export function maandLabelNl(ym: string): string {
  const [jaar, maand] = ym.split('-').map((x) => parseInt(x, 10))
  const naam = NL_MAANDEN[maand - 1] ?? ym
  return `${naam} ${jaar}`
}

/** yyyy-MM-dd → dd-MM-jjjj (NL-weergave zonder tz-conversie). */
export function ymdNaarNlDatum(ymd: string): string {
  const [j, m, d] = ymd.split('-')
  return `${d}-${m}-${j}`
}

/**
 * Resolve een deadline-vorm naar richtdatum + bewoording (FO-332 §3).
 * Gooit bij ontbrekende vorm-parameters — de Zod-schema's horen dat al af te
 * vangen; dit is de laatste verdedigingslinie.
 */
export function resolveDeadlineVorm(input: DeadlineVormInput): DeadlineResolutie {
  switch (input.vorm) {
    case 'KORTE_TERMIJN':
      return {
        richtDatumYmd: ymdPlusDagen(input.vandaagYmd, KORTE_TERMIJN_DAGEN),
        bewoording: 'Korte termijn',
      }
    case 'EINDE_MAAND': {
      const ym = input.maandYm ?? input.vandaagYmd.slice(0, 7)
      return {
        richtDatumYmd: laatsteDagVanMaand(ym),
        bewoording: `Eind ${maandLabelNl(ym)}`,
      }
    }
    case 'NA_MAAND': {
      if (!input.maandYm) throw new Error('NA_MAAND vereist een maand (yyyy-MM)')
      return {
        richtDatumYmd: eersteDagNaMaand(input.maandYm),
        bewoording: `Na ${maandLabelNl(input.maandYm)}`,
      }
    }
    case 'DATUM': {
      if (!input.datumYmd) throw new Error('DATUM vereist een datum (yyyy-MM-dd)')
      return {
        richtDatumYmd: input.datumYmd,
        bewoording: ymdNaarNlDatum(input.datumYmd),
      }
    }
  }
}
